import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, FileCheck2, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { SERVICE_TYPES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
    nome: "",
    empresa: "",
    email: "",
    telefone: "",
    tipo_servico: "",
    origem: "",
    destino: "",
    mensagem: "",
};

const fieldCls =
    "h-12 rounded-none border-white/15 bg-steel text-white placeholder:text-zinc-600 focus-visible:ring-brand";

export const QuoteForm = () => {
    const [form, setForm] = useState(initial);
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const fileRef = useRef(null);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nome.trim() || !form.email.trim()) {
            toast.error("Preencha pelo menos o nome e o email.");
            return;
        }
        setLoading(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            if (file) fd.append("ficheiro", file);
            await axios.post(`${API}/quote-requests`, fd);
            toast.success("Pedido enviado com sucesso. Entraremos em contacto em breve.");
            setForm(initial);
            setFile(null);
        } catch (err) {
            toast.error("Não foi possível enviar o pedido. Tente novamente ou ligue-nos.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
            <div className="grid gap-6 sm:grid-cols-2">
                <div>
                    <Label htmlFor="q-nome" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                        Nome *
                    </Label>
                    <Input id="q-nome" data-testid="quote-form-nome" value={form.nome} onChange={set("nome")} placeholder="O seu nome" className={fieldCls} required />
                </div>
                <div>
                    <Label htmlFor="q-empresa" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                        Empresa
                    </Label>
                    <Input id="q-empresa" data-testid="quote-form-empresa" value={form.empresa} onChange={set("empresa")} placeholder="Nome da empresa" className={fieldCls} />
                </div>
                <div>
                    <Label htmlFor="q-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                        Email *
                    </Label>
                    <Input id="q-email" type="email" data-testid="quote-form-email" value={form.email} onChange={set("email")} placeholder="email@empresa.pt" className={fieldCls} required />
                </div>
                <div>
                    <Label htmlFor="q-telefone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                        Telefone
                    </Label>
                    <Input id="q-telefone" data-testid="quote-form-telefone" value={form.telefone} onChange={set("telefone")} placeholder="+351 ..." className={fieldCls} />
                </div>
                <div>
                    <Label htmlFor="q-servico" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                        Tipo de serviço
                    </Label>
                    <select
                        id="q-servico"
                        data-testid="quote-form-servico"
                        value={form.tipo_servico}
                        onChange={set("tipo_servico")}
                        className="h-12 w-full border border-white/15 bg-steel px-3 text-sm text-white outline-none focus:border-brand"
                    >
                        <option value="">Selecionar…</option>
                        {SERVICE_TYPES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="q-origem" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                            Origem
                        </Label>
                        <Input id="q-origem" data-testid="quote-form-origem" value={form.origem} onChange={set("origem")} placeholder="Local" className={fieldCls} />
                    </div>
                    <div>
                        <Label htmlFor="q-destino" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                            Destino
                        </Label>
                        <Input id="q-destino" data-testid="quote-form-destino" value={form.destino} onChange={set("destino")} placeholder="Local" className={fieldCls} />
                    </div>
                </div>
            </div>
            <div>
                <Label htmlFor="q-mensagem" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                    Mensagem
                </Label>
                <Textarea
                    id="q-mensagem"
                    data-testid="quote-form-mensagem"
                    value={form.mensagem}
                    onChange={set("mensagem")}
                    placeholder="Descreva a operação: materiais, quantidades, prazos…"
                    rows={5}
                    className="rounded-none border-white/15 bg-steel text-white placeholder:text-zinc-600 focus-visible:ring-brand"
                />
            </div>
            <div>
                <Label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                    Ficheiro (opcional)
                </Label>
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="quote-form-file-dropzone"
                    className="flex w-full items-center justify-center gap-3 border border-dashed border-white/20 bg-steel/50 px-6 py-8 text-sm text-zinc-400 transition-colors duration-300 hover:border-brand hover:text-white"
                >
                    {file ? (
                        <>
                            <FileCheck2 className="h-5 w-5 text-brand" />
                            <span data-testid="quote-form-file-name">{file.name}</span>
                        </>
                    ) : (
                        <>
                            <UploadCloud className="h-5 w-5" />
                            Anexar ficheiro — PDF, imagem ou documento
                        </>
                    )}
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid="quote-form-file-input"
                />
            </div>
            <button
                type="submit"
                disabled={loading}
                data-testid="quote-form-submit-button"
                className="flex w-full items-center justify-center gap-3 bg-brand py-5 font-mono text-sm font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-white hover:text-ink disabled:opacity-60"
            >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "A enviar…" : "Pedir Orçamento"}
            </button>
        </form>
    );
};
